import api from './api';

/**
 * #688: Reconciliation > Bank statements. Upload is two calls on the same form: `preview` (dry run,
 * writes nothing) then `upload` (confirm). Amounts are API decimal strings.
 */

export type MatchState = 'unmatched' | 'suggested' | 'matched' | 'ignored';
export type BalanceState = 'ok' | 'mismatch' | 'not_checked';

export const MATCH_STATE_LABEL: Record<MatchState, string> = {
  unmatched: 'Unmatched',
  suggested: 'Suggested',
  matched: 'Matched',
  ignored: 'Ignored',
};

export const MAPPING_FIELDS: { key: BankMappingKey; label: string; hint?: string }[] = [
  { key: 'txn_date', label: 'Transaction date' },
  { key: 'value_date', label: 'Value date' },
  { key: 'narration', label: 'Narration' },
  { key: 'reference', label: 'Reference / UTR' },
  { key: 'debit', label: 'Debit', hint: 'with Credit' },
  { key: 'credit', label: 'Credit', hint: 'with Debit' },
  { key: 'amount', label: 'Amount', hint: 'instead of Debit and Credit' },
  { key: 'dr_cr', label: 'Dr / Cr marker', hint: 'with Amount' },
  { key: 'balance', label: 'Running balance' },
];
export type BankMappingKey =
  | 'txn_date' | 'value_date' | 'narration' | 'reference' | 'debit' | 'credit' | 'amount' | 'dr_cr' | 'balance';
export type BankMapping = Partial<Record<BankMappingKey, string>>;

export interface BankAccountOption {
  id: string;
  name: string;
  code: string;
}

export interface BalanceCheck {
  state: BalanceState;
  credits: string;
  debits: string;
  expected_closing: string | null;
  difference: string | null;
}

export interface PreviewLine {
  row: number;
  txn_date: string;
  narration: string;
  reference: string;
  debit: string;
  credit: string;
  balance: string | null;
  duplicate: boolean;
}

export interface StatementPreview {
  headers: string[];
  raw_headers: string[];
  suggested_mapping: BankMapping;
  saved_mapping: BankMapping;
  /** null: the columns could not be worked out; choose them. */
  mapping: BankMapping | null;
  row_count: number;
  new_count: number;
  duplicate_count: number;
  error_count: number;
  errors: { row: number; message: string }[];
  balance: BalanceCheck | null;
  lines: PreviewLine[];
  suggested_opening?: string | null;
  suggested_closing?: string | null;
  period_start?: string | null;
  period_end?: string | null;
}

export interface BankStatementBatch {
  id: string;
  bank_account: string;
  bank_account_name: string;
  source_file: string;
  period_start: string | null;
  period_end: string | null;
  opening_balance: string | null;
  closing_balance: string | null;
  total_credits: string;
  total_debits: string;
  balance_check: BalanceState;
  row_count: number;
  line_count: number;
  duplicate_count: number;
  error_count: number;
  unmatched_count: number;
  uploaded_by_name: string;
  created_at: string;
}

export interface BankStatementLine {
  id: string;
  batch: string;
  bank_account_name: string;
  txn_date: string;
  value_date: string | null;
  narration: string;
  reference: string;
  debit: string;
  credit: string;
  amount: string;
  balance: string | null;
  match_state: MatchState;
  match_target_type: string;
  match_target_id: string;
  matched_by_name: string;
  matched_at: string | null;
  note: string;
}

export interface LineFilters {
  direction?: '' | 'credit' | 'debit';
  match_state?: '' | MatchState;
  date_from?: string;
  date_to?: string;
  min_amount?: string;
  max_amount?: string;
  q?: string;
  batch?: string;
  page?: number;
}

export interface UploadInput {
  file: File;
  bankAccount: string;
  mapping?: BankMapping | null;
  openingBalance?: string;
  closingBalance?: string;
  confirmBalanceMismatch?: boolean;
  skipErrorRows?: boolean;
}

interface Listing<T> {
  count: number;
  results: T[];
}

function form(input: UploadInput, confirming: boolean): FormData {
  const data = new FormData();
  data.append('file', input.file);
  data.append('bank_account', input.bankAccount);
  if (input.mapping) data.append('mapping', JSON.stringify(input.mapping));
  if (input.openingBalance) data.append('opening_balance', input.openingBalance);
  if (input.closingBalance) data.append('closing_balance', input.closingBalance);
  if (confirming) {
    if (input.confirmBalanceMismatch) data.append('confirm_balance_mismatch', 'true');
    if (input.skipErrorRows) data.append('skip_error_rows', 'true');
  }
  return data;
}

/** The shared axios instance defaults to JSON, which flattens a FormData body and loses the file (#698). */
const MULTIPART = { headers: { 'Content-Type': 'multipart/form-data' } };

const clean = (params: object) => Object.fromEntries(Object.entries(params).filter(([, v]) => v !== '' && v != null));

export const bankStatementsService = {
  async accounts(): Promise<BankAccountOption[]> {
    return (await api.get('/commissions/bank-statements/accounts/')).data;
  },
  async savedMapping(bankAccount: string): Promise<BankMapping> {
    return (await api.get('/commissions/bank-statements/mapping/', { params: { bank_account: bankAccount } })).data.mapping;
  },
  async preview(input: UploadInput): Promise<StatementPreview> {
    return (await api.post('/commissions/bank-statements/preview/', form(input, false), MULTIPART)).data;
  },
  async upload(input: UploadInput): Promise<{ batch: BankStatementBatch; new_count: number; duplicate_count: number; error_count: number }> {
    return (await api.post('/commissions/bank-statements/upload/', form(input, true), MULTIPART)).data;
  },
  async batches(): Promise<Listing<BankStatementBatch>> {
    return (await api.get('/commissions/bank-statements/')).data;
  },
  async lines(filters: LineFilters): Promise<Listing<BankStatementLine>> {
    return (await api.get('/commissions/bank-statement-lines/', { params: clean(filters) })).data;
  },
};
